import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  GiftIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { getLocalStorageItem, successToast, errorToast } from '../../../utils/helper';
import cartService from './cartService';

/**
 * ProductDetail Component (Modal)
 * Displays detailed information about a single product without API calls
 * @returns {JSX.Element|null} ProductDetail modal
 */
const ProductDetail = ({ isOpen = true, product: productProp = null, onClose, onCartUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationStateProduct = location?.state?.product || null;
  const [product, setProduct] = useState(productProp || locationStateProduct || null);
  const [quantity, setQuantity] = useState(1);
  const [selectedDenomination, setSelectedDenomination] = useState(null);
  const [cartItemsCount, setCartItemsCount] = useState(0);

  // Get user data from localStorage
  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';

  useEffect(() => {
    setProduct(productProp || locationStateProduct || null);
  }, [productProp, locationStateProduct]);

  useEffect(() => {
    if (product?.denominations?.length > 0) {
      setSelectedDenomination(product.denominations[0]);
    }
  }, [product]);

  /**
   * Update cart items count on component mount
   */
  useEffect(() => {
    if (!isOpen) return;
    const totalItems = cartService.getTotalCartItems();
    setCartItemsCount(totalItems);
  }, [isOpen]);

  /**
   * Handle quantity change
   */
  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  /**
   * Handle direct quantity input change
   */
  const handleQuantityInputChange = (value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      setQuantity(numValue);
    }
  };

  /**
   * Handle add to cart
   */
  const handleAddToCart = () => {
    if (!selectedDenomination) {
      errorToast('Please select a denomination');
      return;
    }

    if (!product) {
      errorToast('Product not found');
      return;
    }

    const result = cartService.addToCart(product, quantity, selectedDenomination);

    if (result.success) {
      setCartItemsCount(result.uniqueProducts);
      if (onCartUpdate) onCartUpdate(result.uniqueProducts);
      setQuantity(1);
      successToast(`Added ${quantity} x ${product.currencyCode} ${selectedDenomination} to cart!`);
    } else {
      errorToast('Failed to add item to cart. Please try again.');
    }
  };

  /**
   * Navigate back to marketplace
   */
  const handleBackToMarketplace = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(`/${slug}/marketplace`);
  };

  /**
   * Navigate to cart page
   */
  const handleViewCart = () => {
    navigate(`/${slug}/marketplace/cart`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackToMarketplace}
        aria-hidden="true"
      />
      <div className="absolute inset-0 overflow-y-auto">
        <div className="min-h-screen px-4 py-10 flex items-center justify-center">
          <div className="w-full max-w-5xl bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
            {/* Header with Back Button and Cart */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder">
              <button
                onClick={handleBackToMarketplace}
                className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back to Marketplace
              </button>

              <button
                onClick={handleViewCart}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ShoppingCartIcon className="h-5 w-5 mr-2" />
                View Cart
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            </div>

            {!product ? (
              <div className="text-center py-20 px-6">
                <svg
                  className="mx-auto h-16 w-16 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-4 text-xl font-medium text-gray-900 dark:text-white">
                  Product Not Found
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  The product you are looking for could not be found.
                </p>
                <button
                  onClick={handleBackToMarketplace}
                  className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Back to Marketplace
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
                {/* Product Image */}
                <div className="relative">
                  <div className="aspect-w-1 aspect-h-1 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.productName}
                        className="w-full h-full object-contain p-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ display: product.imageUrl ? 'none' : 'flex' }}
                    >
                      <GiftIcon className="h-32 w-32 text-gray-400" />
                    </div>
                  </div>
                  {product.customerDiscountPercentage > 0 && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-lg font-bold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 shadow-md">
                        <TagIcon className="h-5 w-5 mr-1" />
                        {product.customerDiscountPercentage}% OFF
                      </span>
                    </div>
                  )}
                </div>

                {/* Product Information */}
                <div className="flex flex-col">
                  {product.brandName && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide font-semibold">
                      {product.brandName}
                    </p>
                  )}

                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {product.productName}
                  </h1>

                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Product Code: <span className="font-medium">{product.productCode}</span>
                  </p>

                  {product.description && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Description
                      </h2>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {product.description}
                      </p>
                    </div>
                  )}

                  {product.termsAndConditions && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Terms and Conditions
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {product.termsAndConditions}
                      </p>
                    </div>
                  )}

                  {product.redemptionInstructions && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Redemption Instructions
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {product.redemptionInstructions}
                      </p>
                    </div>
                  )}

                  {product.denominations && product.denominations.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Select Value
                      </h2>
                      <div className="flex flex-wrap gap-3">
                        {product.denominations.map((value, index) => {
                          const isSelected = selectedDenomination === value;
                          return (
                            <button
                              key={index}
                              onClick={() => setSelectedDenomination(value)}
                              className={`inline-flex items-center px-6 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                                isSelected
                                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                                  : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-2 border-green-200 dark:border-green-700 hover:border-blue-400 dark:hover:border-blue-500'
                              }`}
                            >
                              {product.currencyCode} {value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Quantity
                    </h2>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center border-2 border-gray-300 dark:border-gray-600 rounded-lg">
                        <button
                          onClick={() => handleQuantityChange(-1)}
                          className="px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-lg transition-colors duration-200"
                        >
                          <MinusIcon className="h-5 w-5" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={quantity}
                          onChange={(e) => handleQuantityInputChange(e.target.value)}
                          className="w-20 text-center border-x-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-lg font-medium focus:outline-none py-3"
                        />
                        <button
                          onClick={() => handleQuantityChange(1)}
                          className="px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg transition-colors duration-200"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                      {selectedDenomination && (
                        <p className="text-lg text-gray-700 dark:text-gray-300">
                          Total:{' '}
                          <span className="font-bold">
                            {product.currencyCode} {selectedDenomination * quantity}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center shadow-lg hover:shadow-xl"
                  >
                    <ShoppingCartIcon className="h-6 w-6 mr-2" />
                    Add to Cart
                  </button>

                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-shoorah-darkBorder">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {product.expiryDate && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Expiry Date</p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {new Date(product.expiryDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {product.deliveryType && (
                        <div>
                          <p className="text-gray-500 dark:text-gray-400">Delivery Type</p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {product.deliveryType}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
